import axios from 'axios';
import pathToRegex from 'path-to-regexp';
import Response from '../response';
import ResponseError from '../response-error';
import { isString } from 'lodash';
import HttpError from 'services/http/rest/response-error/http-error';

const CancelToken = axios.CancelToken;

type APIResponse = Response | ResponseError;

class Request {
  public endpoint;
  public name;
  public path;
  public query;
  public body;
  public params;
  public appendedUrl;
  public cancelRequest;
  public finished;
  public error;
  public payloadPath;

  constructor(properties) {
    const {
      name,
      path,
      query = {},
      body = {},
      endpoint,
      appendedUrl = '',
      params: parameters = {},
      payloadPath,
    } = properties || {};

    this.endpoint = endpoint;
    this.name = name;
    this.path = path;
    this.query = query;
    this.body = body;
    this.params = parameters;
    this.appendedUrl = appendedUrl;
    this.cancelRequest = null;
    this.finished = false;
    this.error = null;
    this.payloadPath = payloadPath;
  }

  getName = () => {
    return this.name;
  };

  setPath = (path) => {
    this.path = path;
    return this;
  };

  setQuery = (query) => {
    this.query = { ...this.query, ...query };
    return this;
  };

  setBody = (body) => {
    this.body = body;
    return this;
  };

  setParams = (parameters) => {
    this.params = parameters;
    return this;
  };

  getSearchQuery = (search) => {
    if (isString(search.query) && isString(search.fields)) {
      return Object.entries(search).reduce(
        (query, [key, value]) => ({
          ...query,
          [`search.${key}`]: value,
        }),
        {},
      );
    }

    return null;
  };

  appendUrl = (url = '') => {
    this.appendedUrl = `${this.appendedUrl}/${url}`.replace('//', '/');
    return this;
  };

  getFullPath = () => {
    let path = `${this.path}/${this.appendedUrl}`.replace('//', '/');
    path = pathToRegex.compile(path)(this.params);

    return path.replace(/\/$/, '');
  };

  cancel = () => {
    if (!this.finished && this.cancelRequest) {
      this.cancelRequest();
    }
    console.error(
      'Could not cancel request. Either the request has finished or no cancel token was created.',
    );
  };

  get = async (data?): Promise<APIResponse> => {
    const { params, ...restOfOptions } = data || {};
    const { search = {}, ...query } = { ...this.query, ...params };
    const searchQuery = this.getSearchQuery(search);

    const options = {
      params: {
        ...query,
        ...(searchQuery ? { ...searchQuery } : {}),
      },
      cancelToken: new CancelToken((cancel) => {
        this.cancelRequest = cancel;
      }),
      ...restOfOptions,
    };

    return this.executeRequest({
      endpoint: this.endpoint,
      method: 'get',
      params: [options],
    });
  };

  post = async (data?: { body: any; options: any }): Promise<APIResponse> => {
    const { body = {}, options = {} } = data || {};

    const postBody = {
      ...this.body,
      ...body,
    };

    const postOptions = {
      cancelToken: new CancelToken((cancel) => {
        this.cancelRequest = cancel;
      }),
      ...options,
    };

    const { endpoint } = this;

    return this.executeRequest({
      endpoint,
      method: 'post',
      params: [postBody, postOptions],
    });
  };

  put = async (data?): Promise<APIResponse> => {
    const { body = {}, options = {} } = data || {};

    const putBody = {
      ...this.body,
      ...body,
    };

    const postOptions = {
      cancelToken: new CancelToken((cancel) => {
        this.cancelRequest = cancel;
      }),
      ...options,
    };

    const { endpoint } = this;

    return this.executeRequest({
      endpoint,
      method: 'put',
      params: [putBody, postOptions],
    });
  };

  patch = async (data?): Promise<APIResponse> => {
    const { body = {}, options = {} } = data || {};

    const patchBody = {
      ...this.body,
      ...body,
    };

    const patchOptions = {
      cancelToken: new CancelToken((cancel) => {
        this.cancelRequest = cancel;
      }),
      ...options,
    };
    const { endpoint } = this;

    return this.executeRequest({
      endpoint,
      method: 'patch',
      params: [patchBody, patchOptions],
    });
  };

  delete = async (config = {}): Promise<APIResponse> => {
    const { endpoint } = this;
    return this.executeRequest({
      endpoint,
      method: 'delete',
      params: [config],
    });
  };

  executeRequest = async ({
    endpoint,
    method,
    params,
  }): Promise<APIResponse> => {
    let response;

    try {
      response = new Response(
        await endpoint[method](this.getFullPath(), ...params),
        this.payloadPath,
      );
    } catch (error) {
      if (error.isAxiosError) {
        response = new ResponseError(new HttpError(error));
      } else {
        response = new ResponseError(error);
      }
    }
    this.finished = true;
    return response;
  };

  getCrudActions = () => ({
    create: this.post,
    update: this.patch,
    delete: this.delete,
    read: this.get,
  });
}

export default Request;
